import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAppDispatch, RootState } from "../../../store";
import {
  fetchAvailablePlans,
  fetchMyAccess,
  fetchLicenseStatus,
  createSubscriptionCheckout,
  changePlan,
  addAddon,
  openBillingPortal,
  cancelSubscription,
  assignTrialLicense,
  syncSubscription,
  fetchPaymentMethods,
  fetchInvoices,
  updateSeats,
  fetchAddonAssignments,
  assignUserToAddon,
  revokeUserFromAddon,
  requestFeatureFromAdmin,
  PaymentMethod,
  Invoice,
  LicensePlan,
  OrgLicenseAccess,
  LicenseStatus,
  AddonAssignment,
  OrgMember,
  AssignmentsResponse,
} from "../../../store/actions/PlanActions";
import { showFeedbackModal } from "../../../store/actions/UserFeedbackActions";
import PlanCard from "./PlanCard";
import AddOnCard from "./AddOnCard";
import CurrentPlanBanner from "./CurrentPlanBanner";
import BillingCard from "./BillingCard";
import InvoicesModal from "./InvoicesModal";
import AddSeatsModal from "./AddSeatsModal";
import AddonAssignmentModal from "./AddonAssignmentModal";
import AddAddonModal from "./AddAddonModal";
import FeatureRequestsTab from "./FeatureRequestsTab";
import TrailUsageBanner from "./TrailUsageBanner";
import ApiLoader from "../../ApiLoader";
import Button from "../TailwindComponents/Button";
import { Dialog, Transition } from "@headlessui/react";
import {
  PiWarningLight,
  PiXLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
  PiPuzzlePieceLight,
  PiSparkleLight,
  PiFileTextLight,
  PiFunnelLight,
  PiChartBarLight,
  PiBookOpenLight,
  PiHardDriveLight,
  PiDatabaseLight,
  PiClockCountdownLight,
  PiArrowRightLight,
  PiEnvelopeSimpleLight,
} from "react-icons/pi";
import { showSidePanel } from "../../../store/actions/sidePanelActions";
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, plansBillingTourSteps } from '../FeatureTour';

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const plansRef = useRef<HTMLDivElement>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'plans-billing',
    steps: plansBillingTourSteps,
    autoStartDelay: 600,
  });

  /* ─── State ─── */
  const [plans, setPlans] = useState<LicensePlan[]>([]);
  const [addons, setAddons] = useState<LicensePlan[]>([]);
  const [access, setAccess] = useState<OrgLicenseAccess | null>(null);
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [seatCount, setSeatCount] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);
  const [addonLoading, setAddonLoading] = useState<number | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoicesModal, setInvoicesModal] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  /* Seat management */
  const [seatsModal, setSeatsModal] = useState(false);
  const [seatMembers, setSeatMembers] = useState<OrgMember[]>([]);

  /* Addon assignment */
  const [assignmentModal, setAssignmentModal] = useState(false);
  const [assignmentAddon, setAssignmentAddon] = useState<AddonAssignment | null>(null);
  const [assignmentMembers, setAssignmentMembers] = useState<OrgMember[]>([]);
  const [assignmentsData, setAssignmentsData] = useState<AssignmentsResponse | null>(null);

  /* Add addon purchase */
  const [addAddonModal, setAddAddonModal] = useState(false);
  const [addAddonTarget, setAddAddonTarget] = useState<LicensePlan | null>(null);

  /* Plan change confirmation */
  const [changePlanModal, setChangePlanModal] = useState(false);
  const [changePlanTarget, setChangePlanTarget] = useState<LicensePlan | null>(null);
  const [changePlanLoading, setChangePlanLoading] = useState(false);

  /* Subtab: "plans" (default) or "requests" (admin-only) */
  const [subtab, setSubtab] = useState<"plans" | "requests">("plans");

  /* ─── Derived: admin status ─── */
  const isOrgAdmin = userInfo?.is_org_admin === true;

  /* ─── Read subtab from URL (for notification deep-links) ─── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSubtab = params.get("subtab");
    if (urlSubtab === "requests" && isOrgAdmin) {
      setSubtab("requests");
    }
  }, [isOrgAdmin]);

  /* ─── Load data ─── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, accessData, statusData] = await Promise.all([
        dispatch(fetchAvailablePlans()),
        dispatch(fetchMyAccess()),
        dispatch(fetchLicenseStatus()),
      ]);
      console.log("[PlansPage] plansData:", plansData);
      console.log("[PlansPage] accessData:", accessData);
      console.log("[PlansPage] statusData:", statusData);

      if (plansData) {
        setPlans(plansData.plans || []);
        setAddons(plansData.addons || []);
      }
      if (accessData) setAccess(accessData);
      if (statusData) setStatus(statusData);

      // Fetch payment methods if org has any active license (including trial — they may have added a card via portal)
      if (statusData?.has_active_license) {
        const orgIdVal = userInfo?.OrganizationID || userInfo?.organization_id;
        if (orgIdVal) {
          const pms = await dispatch(fetchPaymentMethods());
          if (pms) setPaymentMethods(pms);
        }

        // Pre-fetch addon assignments so we can show seat usage on cards
        const assignData = await dispatch(fetchAddonAssignments());
        if (assignData) setAssignmentsData(assignData);
      }

      // Auto-assign trial if org has no active license
      if (statusData && !statusData.has_active_license) {
        console.log("[PlansPage] No active license — assigning trial...");
        const trialResult = await dispatch(assignTrialLicense());
        console.log("[PlansPage] Trial assignment result:", trialResult);
        if (trialResult?.success) {
          const [newAccess, newStatus] = await Promise.all([
            dispatch(fetchMyAccess()),
            dispatch(fetchLicenseStatus()),
          ]);
          if (newAccess) setAccess(newAccess);
          if (newStatus) setStatus(newStatus);
        }
      }
    } catch (err) {
      console.error("[PlansPage] loadAll error:", err);
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ─── Derived ─── */
  const orgId = userInfo?.OrganizationID || userInfo?.organization_id;
  const userEmail = userInfo?.Email || userInfo?.email || "";

  const currentBasePlanName = useMemo(() => {
    if (access?.license_info?.plan_name) return access.license_info.plan_name;
    if (status?.base_plan) return status.base_plan;
    return null;
  }, [access, status]);

  const activeAddonNames = useMemo(() => {
    const licInfo = access?.license_info;
    // Non-admin users should only see addons assigned to them, not all org addons
    const names = (!isOrgAdmin && licInfo?.user_addons)
      ? licInfo.user_addons
      : (licInfo?.active_addons || status?.active_addons || []);
    return new Set(names);
  }, [access, status, isOrgAdmin]);

  const currentPlanSortOrder = useMemo(() => {
    if (!currentBasePlanName) return undefined;
    const match = plans.find((p) => p.name === currentBasePlanName);
    return match?.sort_order;
  }, [currentBasePlanName, plans]);

  const currentBasePlan = useMemo(() => {
    if (!currentBasePlanName) return null;
    return plans.find((p) => p.name === currentBasePlanName) || null;
  }, [currentBasePlanName, plans]);

  /** Map addon license_id → assignment data for seat usage display */
  const addonAssignmentMap = useMemo(() => {
    const map = new Map<number, { assignedCount: number; totalSeats: number }>();
    if (assignmentsData?.addons) {
      for (const a of assignmentsData.addons) {
        map.set(a.license_id, { assignedCount: a.assigned_count, totalSeats: a.quantity });
      }
    }
    return map;
  }, [assignmentsData]);

  /** Full-plan value nudge: suggest upgrading when add-on costs approach the Full plan price */
  const fullPlanNudge = useMemo(() => {
    const fullPlan = plans.find((p) => p.type === "base" && (p.name || "").toLowerCase().includes("full"));
    if (!fullPlan) return null;
    // Don't nudge if already on Full plan or higher
    if (currentBasePlanName && currentBasePlanName.toLowerCase().includes("full")) return null;
    // Count active add-ons cost
    const activeAddonPlans = addons.filter(
      (a) => activeAddonNames.has(a.display_name) || activeAddonNames.has(a.name)
    );
    if (activeAddonPlans.length < 2) return null; // Only nudge with 2+ add-ons
    const addonTotal =
      billingPeriod === "monthly"
        ? activeAddonPlans.reduce((sum, a) => sum + a.price_monthly, 0)
        : activeAddonPlans.reduce((sum, a) => sum + a.price_annual, 0);
    const baseCost = currentBasePlan
      ? billingPeriod === "monthly"
        ? currentBasePlan.price_monthly
        : currentBasePlan.price_annual
      : 0;
    const currentTotal = baseCost + addonTotal;
    const fullPrice = billingPeriod === "monthly" ? fullPlan.price_monthly : fullPlan.price_annual;
    // Only nudge if Full plan is cheaper or within 20% of what they're already paying
    if (fullPrice > currentTotal * 1.2) return null;
    const savings = currentTotal - fullPrice;
    return { fullPlan, currentTotal, fullPrice, savings, addonCount: activeAddonPlans.length };
  }, [plans, addons, activeAddonNames, currentBasePlanName, currentBasePlan, billingPeriod]);

  /* ─── Handlers ─── */
  const hasActiveStripeSubscription = useMemo(() => {
    return access?.licenses?.some((l) => l.stripe_subscription_id && l.is_active) ?? false;
  }, [access]);

  const handleSelectPlan = async (plan: LicensePlan) => {
    if (!orgId) {
      dispatch(showFeedbackModal({ showModal: true, message: t("Organization not found"), modalType: "error", duration: 3000 }));
      return;
    }
    // Non-admin users can only request upgrades
    if (!isOrgAdmin) {
      const sectionLabel = plan.display_name || plan.name;
      dispatch(requestFeatureFromAdmin(sectionLabel, t("I'd like to upgrade to the {{plan}} plan.", { plan: sectionLabel })));
      return;
    }
    // If org already has an active Stripe subscription, modify it instead of creating a new one
    if (hasActiveStripeSubscription) {
      setChangePlanTarget(plan);
      setChangePlanModal(true);
      return;
    }
    // No subscription yet — go through Stripe Checkout
    setCheckoutLoading(plan.id);
    try {
      const successUrl = `${window.location.origin}/account?tab=plans&success=true`;
      const cancelUrl = `${window.location.origin}/account?tab=plans&canceled=true`;
      const result = await dispatch(
        createSubscriptionCheckout({
          license_ids: [plan.id],
          email: userEmail,
          billing_period: billingPeriod,
          seat_count: seatCount,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.url) {
        window.location.assign(result.url);
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleConfirmChangePlan = async () => {
    if (!orgId || !changePlanTarget) return;
    setChangePlanLoading(true);
    try {
      const result = await dispatch(
        changePlan({
          new_license_id: changePlanTarget.id,
          billing_period: billingPeriod,
        })
      );
      if (result) {
        setChangePlanModal(false);
        setChangePlanTarget(null);
        await loadAll();
      }
    } finally {
      setChangePlanLoading(false);
    }
  };

  const handleAddAddon = async (addon: LicensePlan) => {
    // Non-admin users can only request addons
    if (!isOrgAdmin) {
      const label = addon.display_name || addon.name;
      dispatch(requestFeatureFromAdmin(label, t("I'd like to add the {{addon}} add-on.", { addon: label })));
      return;
    }
    setAddAddonTarget(addon);
    setAddAddonModal(true);
  };

  const handleConfirmAddAddon = async (
    addon: LicensePlan,
    seatCount: number,
    applyTeamDiscount: boolean
  ) => {
    if (!orgId) return;
    setAddonLoading(addon.id);
    try {
      const successUrl = `${window.location.origin}/account?tab=plans&success=true`;
      const cancelUrl = `${window.location.origin}/account?tab=plans&canceled=true`;
      const result = await dispatch(
        addAddon({
          addon_license_id: addon.id,
          billing_period: billingPeriod,
          seat_count: seatCount,
          apply_team_discount: applyTeamDiscount,
          email: userEmail,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.requires_checkout && result?.url) {
        window.location.assign(result.url);
        return;
      }
      if (result) await loadAll();
    } finally {
      setAddonLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!orgId) return;
    setPortalLoading(true);
    try {
      const result = await dispatch(
        openBillingPortal({
          return_url: window.location.href,
        })
      );
      if (result?.url) {
        window.location.assign(result.url);
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const handleOpenInvoices = async () => {
    if (!orgId) return;
    setInvoicesModal(true);
    setInvoicesLoading(true);
    try {
      const result = await dispatch(fetchInvoices());
      setInvoices(result || []);
    } finally {
      setInvoicesLoading(false);
    }
  };

  /* ── Seat Management ── */
  const handleOpenSeatsModal = async () => {
    // Fetch members so we can show who occupies seats
    const data = await dispatch(fetchAddonAssignments());
    if (data?.members) {
      setSeatMembers(data.members);
    }
    setSeatsModal(true);
  };

  const handleUpdateSeats = async (newQuantity: number) => {
    if (!orgId || !access) {
      dispatch(showFeedbackModal({ showModal: true, message: t("Organization data not loaded"), modalType: "error", duration: 3000 }));
      throw new Error("no_org");
    }
    // Find the base plan's org_license_id
    const baseLicense = access.licenses.find((l) => ["base", "trial", "pilot"].includes(l.type));
    if (!baseLicense) {
      dispatch(showFeedbackModal({ showModal: true, message: t("No active base license found"), modalType: "error", duration: 3000 }));
      throw new Error("no_license");
    }
    const result = await dispatch(
      updateSeats({
        org_license_id: baseLicense.id,
        new_quantity: newQuantity,
      })
    );
    if (result) {
      await loadAll();
    } else {
      throw new Error("update_failed");
    }
  };

  /* ── Addon Assignment ── */
  const handleManageAssignments = async (addon: LicensePlan) => {
    // Fetch assignment data, then open modal for the matching addon
    const data = await dispatch(fetchAddonAssignments());
    if (!data) return;
    setAssignmentsData(data);
    setAssignmentMembers(data.members || []);
    const match = data.addons.find((a: AddonAssignment) => a.license_id === addon.id);
    if (match) {
      setAssignmentAddon(match);
      setAssignmentModal(true);
    }
  };

  const handleManageBasePlanSeats = async () => {
    const data = await dispatch(fetchAddonAssignments());
    if (!data) return;
    setAssignmentsData(data);
    setAssignmentMembers(data.members || []);
    if (data.base_plan) {
      setAssignmentAddon(data.base_plan);
      setAssignmentModal(true);
    }
  };

  const handleAssignUser = async (orgLicenseId: number, userId: number): Promise<boolean> => {
    const result = await dispatch(assignUserToAddon({ org_license_id: orgLicenseId, user_id: userId }));
    return !!result;
  };

  const handleRevokeUser = async (orgLicenseId: number, userId: number): Promise<boolean> => {
    const result = await dispatch(revokeUserFromAddon({ org_license_id: orgLicenseId, user_id: userId }));
    return !!result;
  };

  const handleCancelSubscription = async () => {
    if (!orgId) return;
    setCancelLoading(true);
    try {
      const result = await dispatch(
        cancelSubscription({ at_period_end: true })
      );
      if (result) {
        setCancelModal(false);
        await loadAll();
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleViewDetails = (plan: LicensePlan) => {
    const isBase = plan.type === "base";
    const isCurrent = isBase ? currentBasePlanName === plan.name : false;
    const isActiveAddon = !isBase && (activeAddonNames.has(plan.display_name) || activeAddonNames.has(plan.name));

    // Pick the right header icon
    const getHeaderIcon = () => {
      if (isBase) {
        const key = (plan.name || plan.display_name).toLowerCase();
        if (key.includes("starter")) return <PiShieldCheckLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
        if (key.includes("professional")) return <PiRocketLaunchLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
        if (key.includes("enterprise")) return <PiCrownLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
        return <PiPackageLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
      }
      const name = (plan.name || plan.display_name).toLowerCase();
      const sections = plan.feature_gates?.sections || [];
      const ai = plan.feature_gates?.ai_features || [];
      if (ai.length > 0 || name.includes("ai") || sections.some((s: string) => ["research", "tables"].includes(s)))
        return <PiSparkleLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
      if (name.includes("template") || sections.includes("templates")) return <PiFileTextLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
      if (name.includes("pipeline") || sections.includes("client_pipeline")) return <PiFunnelLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
      if (name.includes("analytics") || sections.includes("analytics")) return <PiChartBarLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
      if (name.includes("knowledge") || sections.includes("data_sources")) return <PiBookOpenLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
      return <PiPuzzlePieceLight className="h-5 w-5 text-gray-900 dark:text-gray-100" />;
    };

    dispatch(
      showSidePanel({
        type: "licenseDetail" as any,
        showSidePanel: true,
        width: "40%",
        message: isBase ? t("Base License") : t("Add-on"),
        icon: getHeaderIcon(),
        plan,
        billingPeriod,
        isCurrentPlan: isCurrent,
        isActive: isActiveAddon,
        currentPlanSortOrder,
        seatCount: isCurrent ? (access?.license_info?.seat_count || status?.seat_count || 1) : undefined,
        seatsUsed: isCurrent ? (access?.license_info?.seats_used || status?.user_count || 0) : undefined,
        onUpdateSeats: isCurrent ? handleUpdateSeats : undefined,
        onAction: (p: LicensePlan, upgradeSeatCount?: number) => {
          if (isBase) {
            if (upgradeSeatCount && upgradeSeatCount > 1) setSeatCount(upgradeSeatCount);
            handleSelectPlan(p);
          } else {
            handleAddAddon(p);
          }
        },
      } as any)
    );
  };

  /* ─── Success / Canceled query params ─── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      // Sync subscription from Stripe into DB, then reload license data.
      // Must wait for orgId — on redirect it may not be hydrated on first render.
      if (orgId) {
        dispatch(syncSubscription() as any).then(() => {
          loadAll();
        });
        dispatch(showFeedbackModal({
          showModal: true,
          message: t("Subscription activated successfully!"),
          modalType: "success",
          duration: 5000,
        }));
        history.replace("/account?tab=plans");
      }
      // If orgId isn't ready yet, this effect will re-run when it becomes available
      return;
    }
    if (params.get("canceled") === "true") {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t("Checkout was canceled"),
        modalType: "info" as any,
        duration: 3000,
      }));
      history.replace("/account?tab=plans");
    }
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For non-admins: only show current plan + the one they can upgrade to.
  // Must be before any early returns to preserve hook order.
  const isOnPaidPlan = currentBasePlanName === 'pons_starter' || currentBasePlanName === 'pons_full';
  const isOnFull = currentBasePlanName === 'pons_full';
  const isOnPilot = status?.plan_type === 'pilot';

  const nonAdminVisiblePlans = useMemo(() => {
    return plans
      .filter((p) => p.type === "base" || p.type === "trial")
      .filter((p) => {
        if (p.name === currentBasePlanName) return true;
        if (p.type === "trial" && isOnPaidPlan) return false;
        if (isOnFull || isOnPilot) return false;
        if (currentPlanSortOrder !== undefined && p.sort_order <= currentPlanSortOrder) return false;
        return true;
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [plans, currentBasePlanName, isOnPaidPlan, isOnFull, isOnPilot, currentPlanSortOrder]);

  /* ─── Render ─── */
  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
        ))}
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6 overflow-auto custom-scrollbar-main pt-4">
      {/* ══════════════════════════════════════════════════════
          NON-ADMIN VIEW — simplified: only their plan + request options
         ══════════════════════════════════════════════════════ */}
      {!isOrgAdmin ? (
      <>
      <div className="space-y-2 px-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t("My Plan")}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("Your current plan and available features")}
        </p>
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      <div className="px-4">
        <CurrentPlanBanner
          access={access}
          status={status}
          isOrgAdmin={false}
          onChangePlan={scrollToPlans}
        />
      </div>

      {/* Trail usage for non-admins */}
      {status?.is_trial && (
        <div className="px-4">
          <TrailUsageBanner />
        </div>
      )}

      {/* User's active add-ons */}
      {access?.license_info?.user_addons && access.license_info.user_addons.length > 0 && (
        <div className="px-4">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{t("Your Add-ons")}</p>
          <div className="flex flex-wrap gap-2">
            {access.license_info.user_addons.map((addonName) => (
              <span key={addonName} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 text-xs font-medium text-primary-700 dark:text-primary-300">
                <PiPuzzlePieceLight className="h-3.5 w-3.5" />
                {addonName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Upgrade options — only show if there's something to upgrade to */}
      {nonAdminVisiblePlans.length > 1 && (
        <>
        <hr className="border-gray-200 dark:border-gray-700" />
        <div className="px-4">
          <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4 items-stretch">
            {nonAdminVisiblePlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingPeriod={billingPeriod}
                isCurrentPlan={currentBasePlanName === plan.name}
                currentPlanSortOrder={currentPlanSortOrder}
                onSelect={handleSelectPlan}
                isLoading={checkoutLoading === plan.id}
                onViewDetails={handleViewDetails}
                isOrgAdmin={false}
              />
            ))}
          </div>
        </div>
        </>
      )}

      {/* Add-ons to request (Starter only) */}
      {addons.length > 0 && currentBasePlanName === 'pons_starter' && (
        <div className="px-4">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{t("Available Add-ons")}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {t("Request add-ons from your admin to extend your capabilities")}
          </p>
          <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
            {addons
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((addon) => (
                <AddOnCard
                  key={addon.id}
                  addon={addon}
                  billingPeriod={billingPeriod}
                  isActive={activeAddonNames.has(addon.display_name) || activeAddonNames.has(addon.name)}
                  onAdd={handleAddAddon}
                  onViewDetails={handleViewDetails}
                  isOrgAdmin={false}
                />
              ))}
          </div>
        </div>
      )}
      </>
      ) : (
      /* ══════════════════════════════════════════════════════
          ADMIN VIEW — full management
         ══════════════════════════════════════════════════════ */
      <>
      <div className="space-y-2 px-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t("Plans & Billing")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("Manage your subscription, add-ons, and billing details")}
            </p>
          </div>
          {/* Subtab switcher — admin-only */}
          <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-0.5">
            <button
              onClick={() => setSubtab("plans")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subtab === "plans"
                  ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
              }`}
            >
              {t("Plans")}
            </button>
            <button
              onClick={() => setSubtab("requests")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                subtab === "requests"
                  ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
              }`}
            >
              <PiEnvelopeSimpleLight className="w-3.5 h-3.5" />
              {t("Requests")}
            </button>
          </div>
        </div>
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      {/* ══════════════════════════════════════════════════════
          REQUESTS SUBTAB (admin only)
         ══════════════════════════════════════════════════════ */}
      {subtab === "requests" ? (
        <FeatureRequestsTab addons={addons} />
      ) : (
      <>
      <div data-tour-id="plans-current" className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch px-4">
        {/* ── Current Plan ── */}
        <CurrentPlanBanner
          access={access}
          status={status}
          isOrgAdmin={true}
          onChangePlan={scrollToPlans}
          onAddSeats={handleOpenSeatsModal}
          onManageSeats={handleManageBasePlanSeats}
        />

        {/* ── Billing ── */}
        <BillingCard
          access={access}
          status={status}
          onManageBilling={handleManageBilling}
          onViewInvoices={handleOpenInvoices}
          isPortalLoading={portalLoading}
          paymentMethods={paymentMethods}
        />
      </div>

      {/* ── Trail Usage Banner — shown when org is on a trial plan ── */}
      {status?.is_trial && (
        <div className="px-4">
          <TrailUsageBanner />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          BOTTOM SECTION — Available Plans & Add-Ons
         ══════════════════════════════════════════════════════ */}
      <hr className="border-gray-200 dark:border-gray-700" />

      <div className="space-y-2 px-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t("Available Plans")}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t("Browse and compare plans to find the right fit for your organization")}
        </p>
      </div>

      {/* ── Billing toggle ── */}
      <div ref={plansRef} className="flex items-center gap-3 px-4">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t("Billing")}</span>
        <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-0.5">
          <button
            onClick={() => setBillingPeriod("monthly")}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              billingPeriod === "monthly"
                ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
            }`}
          >
            {t("Monthly")}
          </button>
          <button
            onClick={() => setBillingPeriod("annual")}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
              billingPeriod === "annual"
                ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
            }`}
          >
            {t("Annual")}
            <span className="text-xs text-green-600 dark:text-green-400">−17%</span>
          </button>
        </div>
      </div>

      {/* ── Plan Cards ── */}
      {plans.length > 0 ? (
        <div className="px-4">
          <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4 items-stretch">
            {plans
              .filter((p) => p.type === "base" || p.type === "trial")
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  billingPeriod={billingPeriod}
                  isCurrentPlan={currentBasePlanName === plan.name}
                  currentPlanSortOrder={currentPlanSortOrder}
                  onSelect={handleSelectPlan}
                  isLoading={checkoutLoading === plan.id}
                  onViewDetails={handleViewDetails}
                  isOrgAdmin={isOrgAdmin}
                />
              ))}
          </div>
        </div>
      ) : (
        <div className="border rounded-lg bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            {t("No plans available for your organization type")}
          </p>
        </div>
      )}

      {/* ── Add-Ons — only shown for Starter plan (addon-eligible) ── */}
      {addons.length > 0 && currentBasePlanName === 'pons_starter' && (
        <div className="px-4">
          <div className="space-y-1 mb-3">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t("Add-Ons")}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("Extend your plan with additional capabilities")}
            </p>
          </div>

          {/* Full-plan value nudge */}
          {fullPlanNudge && (
            <div className="flex items-center gap-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-3 mb-4">
              <PiCrownLight className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t("Save with")} {fullPlanNudge.fullPlan.display_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("You're paying")} ${fullPlanNudge.currentTotal.toFixed(0)}/{t("mo")} {t("for your plan + {{count}} add-ons", { count: fullPlanNudge.addonCount })}.{" "}
                  {fullPlanNudge.savings > 0
                    ? t("Upgrade to {{plan}} at ${{price}}/mo and save ${{savings}}/mo — everything included.", {
                        plan: fullPlanNudge.fullPlan.display_name,
                        price: fullPlanNudge.fullPrice.toFixed(0),
                        savings: fullPlanNudge.savings.toFixed(0),
                      })
                    : t("Get {{plan}} at ${{price}}/mo with everything included.", {
                        plan: fullPlanNudge.fullPlan.display_name,
                        price: fullPlanNudge.fullPrice.toFixed(0),
                      })}
                </p>
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleSelectPlan(fullPlanNudge.fullPlan)}
                className="flex-shrink-0"
              >
                {t("Upgrade")}
                <PiArrowRightLight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
            {addons
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((addon) => {
                const addonAssign = addonAssignmentMap.get(addon.id);
                return (
                  <AddOnCard
                    key={addon.id}
                    addon={addon}
                    billingPeriod={billingPeriod}
                    isActive={activeAddonNames.has(addon.display_name) || activeAddonNames.has(addon.name)}
                    onAdd={handleAddAddon}
                    isLoading={addonLoading === addon.id}
                    onViewDetails={handleViewDetails}
                    onManageAssignments={handleManageAssignments}
                    teamSize={access?.license_info?.seat_count || status?.seat_count || 1}
                    assignedCount={addonAssign?.assignedCount}
                    totalSeats={addonAssign?.totalSeats}
                    isOrgAdmin={isOrgAdmin}
                  />
                );
              })}
          </div>
        </div>
      )}

      {/* ── Usage & Limits ── */}
      <div className="px-4">
        <div className="space-y-1 mb-3">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t("Usage & Limits")}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t("Control storage, data sources, and data retention for your organization")}
          </p>
        </div>
        <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4 items-start">
          {/* Increase Storage */}
          <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <PiHardDriveLight className="h-4 w-4 text-gray-900 dark:text-gray-100 flex-shrink-0" />
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{t("Increase Storage")}</h4>
              </div>
              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded">
                {t("Coming Soon")}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("Add more storage capacity for your files, documents, and case data.")}
            </p>
          </div>

          {/* More Data Sources */}
          <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <PiDatabaseLight className="h-4 w-4 text-gray-900 dark:text-gray-100 flex-shrink-0" />
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{t("More Data Sources")}</h4>
              </div>
              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded">
                {t("Coming Soon")}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("Connect additional knowledge bases, external databases, and document repositories.")}
            </p>
          </div>

          {/* Retention */}
          <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 p-5 relative">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <PiClockCountdownLight className="h-4 w-4 text-gray-900 dark:text-gray-100 flex-shrink-0" />
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{t("Retention Period")}</h4>
              </div>
              <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded">
                {t("Coming Soon")}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("Configure how long your organization's data is retained. Set retention policies in years to comply with regulatory requirements.")}
            </p>
          </div>
        </div>
      </div>

      {/* ── Cancel Subscription ── */}
      {isOrgAdmin && status?.has_active_license && !status.is_trial && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t("Need to pause or stop your subscription? You can cancel anytime — your plan stays active until the end of the billing period.")}
          </p>
          <Button
            variant="outline"
            size="small"
            onClick={() => setCancelModal(true)}
            className="flex-shrink-0 ml-4 !text-gray-400 hover:!text-red-500 !border-gray-300 dark:!border-gray-600 hover:!border-red-400"
          >
            {t("Cancel Subscription")}
          </Button>
        </div>
      )}
      </>
      )}
      </>
      )}

      {/* ── Cancel Modal ── */}
      <Transition show={cancelModal} as={React.Fragment}>
        <Dialog onClose={() => setCancelModal(false)} className="relative z-50">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Dialog.Title className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {t("Cancel Subscription?")}
                    </Dialog.Title>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t("This action cannot be undone")}
                    </p>
                  </div>
                  <button
                    onClick={() => setCancelModal(false)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <PiXLight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>

                <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-3 mb-4 text-xs text-yellow-800 dark:text-yellow-300">
                  {t(
                    "Your access will continue until the end of your current billing period. After that, your organization will lose access to licensed features."
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="neutral"
                    size="small"
                    onClick={() => setCancelModal(false)}
                  >
                    {t("Keep Subscription")}
                  </Button>
                  <Button
                    variant="destructive"
                    size="small"
                    onClick={handleCancelSubscription}
                    isLoading={cancelLoading}
                  >
                    {t("Cancel Subscription")}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ── Invoices Modal ── */}
      <InvoicesModal
        open={invoicesModal}
        onClose={() => setInvoicesModal(false)}
        invoices={invoices}
        loading={invoicesLoading}
      />

      {/* ── Change Plan Confirmation Modal ── */}
      <Transition show={changePlanModal} as={React.Fragment}>
        <Dialog onClose={() => { setChangePlanModal(false); setChangePlanTarget(null); }} className="relative z-50">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg p-4">
                {changePlanTarget && (() => {
                  const isDowngrade = currentPlanSortOrder !== undefined && changePlanTarget.sort_order < currentPlanSortOrder;
                  const newPrice = billingPeriod === "monthly" ? changePlanTarget.price_monthly : changePlanTarget.price_annual;
                  const currentPrice = currentBasePlan
                    ? (billingPeriod === "monthly" ? currentBasePlan.price_monthly : currentBasePlan.price_annual)
                    : 0;

                  // Identify features being lost on downgrade
                  const currentSections = new Set(currentBasePlan?.feature_gates?.sections || []);
                  const newSections = new Set(changePlanTarget.feature_gates?.sections || []);
                  const lostSections = [...currentSections].filter((s) => !newSections.has(s));
                  const currentAi = new Set(currentBasePlan?.feature_gates?.ai_features || []);
                  const newAi = new Set(changePlanTarget.feature_gates?.ai_features || []);
                  const lostAi = [...currentAi].filter((f) => !newAi.has(f));

                  return (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <Dialog.Title className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {isDowngrade ? t("Downgrade to {{plan}}?", { plan: changePlanTarget.display_name }) : t("Upgrade to {{plan}}?", { plan: changePlanTarget.display_name })}
                          </Dialog.Title>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {isDowngrade
                              ? t("Your plan will change at the end of the current billing period")
                              : t("You'll be charged the prorated difference immediately")}
                          </p>
                        </div>
                        <button
                          onClick={() => { setChangePlanModal(false); setChangePlanTarget(null); }}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          <PiXLight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      </div>

                      {/* Price comparison */}
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 mb-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t("Current plan")}</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">${currentPrice.toFixed(0)}/{t("mo")}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">{t("New plan")}</span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">${newPrice.toFixed(0)}/{t("mo")}</span>
                        </div>
                      </div>

                      {/* Feature loss warning for downgrades */}
                      {isDowngrade && (lostSections.length > 0 || lostAi.length > 0) && (
                        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-3 mb-3 text-xs">
                          <div className="flex items-start gap-2">
                            <PiWarningLight className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                                {t("You will lose access to the following features:")}
                              </p>
                              <ul className="space-y-0.5 text-yellow-700 dark:text-yellow-400">
                                {lostSections.map((s) => (
                                  <li key={s}>• {s.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</li>
                                ))}
                                {lostAi.map((f) => (
                                  <li key={f}>• AI: {f.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="neutral"
                          size="small"
                          onClick={() => { setChangePlanModal(false); setChangePlanTarget(null); }}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button
                          variant={isDowngrade ? "outline" : "primary"}
                          size="small"
                          onClick={handleConfirmChangePlan}
                          isLoading={changePlanLoading}
                        >
                          {isDowngrade ? t("Confirm Downgrade") : t("Confirm Upgrade")}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Add Seats Modal removed — seats are now assigned directly, billing adjusts automatically */}

      {/* ── Addon Assignment Modal ── */}
      <AddonAssignmentModal
        open={assignmentModal}
        onClose={async () => {
          setAssignmentModal(false);
          setAssignmentAddon(null);
          // Refresh assignments data so card seat counts update
          const data = await dispatch(fetchAddonAssignments());
          if (data) setAssignmentsData(data);
        }}
        addon={assignmentAddon}
        members={assignmentMembers}
        onAssign={handleAssignUser}
        onRevoke={handleRevokeUser}
      />

      {/* ── Add Addon Purchase Modal ── */}
      <AddAddonModal
        open={addAddonModal}
        onClose={() => { setAddAddonModal(false); setAddAddonTarget(null); }}
        addon={addAddonTarget}
        billingPeriod={billingPeriod}
        teamSize={access?.license_info?.seat_count || status?.seat_count || 1}
        onConfirm={handleConfirmAddAddon}
        fullPlanSuggestion={(() => {
          const fullPlan = plans.find((p) => p.type === "base" && (p.name || "").toLowerCase().includes("full"));
          if (!fullPlan || !addAddonTarget) return null;
          if (currentBasePlanName && currentBasePlanName.toLowerCase().includes("full")) return null;
          const activeAddonCost = addons
            .filter((a) => activeAddonNames.has(a.display_name) || activeAddonNames.has(a.name))
            .reduce((sum, a) => sum + (billingPeriod === "monthly" ? a.price_monthly : a.price_annual), 0);
          const baseCost = currentBasePlan ? (billingPeriod === "monthly" ? currentBasePlan.price_monthly : currentBasePlan.price_annual) : 0;
          const newAddonCost = billingPeriod === "monthly" ? addAddonTarget.price_monthly : addAddonTarget.price_annual;
          return {
            fullPlan,
            fullPlanPrice: billingPeriod === "monthly" ? fullPlan.price_monthly : fullPlan.price_annual,
            currentTotalWithAddon: baseCost + activeAddonCost + newAddonCost,
          };
        })()}
        onUpgradeToFull={handleSelectPlan}
      />
    </div>
    <FeatureTour
      steps={plansBillingTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default PlansPage;
